import {
  Box,
  Drawer,
  alpha,
  styled,
  useTheme,
  lighten,
  darken,
} from '@mui/material';
import { useNavigate } from 'react-router';
import Scrollbar from '@/content/components/common/Scrollbar';
import { useDashboardLayout } from '@/contexts/DashboardLayoutContext';
import SidebarMenu from '@/content/layouts/DashboardLayout/Sidebar/SidebarMenu';
import Logo from '@/content/components/common/LogoSign';
import PrivacyPolicyBtn from '@/content/components/common/Button/PrivacyPolicyBtn';
import ContactSupportBtn from '@/content/components/common/Button/ContactSupportBtn';
import FRONTEND_ROUTES from '@/consts/frontendRoutes';

const SidebarWrapper = styled(Box)(
  ({ theme }) => `
        width: ${theme.sidebar.width};
        min-width: ${theme.sidebar.width};
        color: ${theme.colors.alpha.white[70]};
        position: relative;
        z-index: 7;
        height: 100%;
        display: flex;
        flex-direction: column;
`,
);

const SidebarFooter = () => (
  <Box
    sx={{
      flexShrink: 0,
      color: 'white',
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      py: 2,
      px: 2,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <ContactSupportBtn />
      <PrivacyPolicyBtn />
    </Box>
  </Box>
);

const Sidebar = () => {
  const { sidebarToggle, closeSidebar } = useDashboardLayout();
  const theme = useTheme();
  const navigate = useNavigate();

  const handleLogoClick = () => {
    navigate(FRONTEND_ROUTES.DASHBOARD);
    closeSidebar();
  };

  const handleLogoKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleLogoClick();
    }
  };

  return (
    <>
      <SidebarWrapper
        sx={{
          display: {
            xs: 'none',
            lg: 'flex',
          },
          position: 'fixed',
          left: 0,
          top: 0,
          // px: 2,
          background:
            theme.palette.mode === 'dark'
              ? alpha(lighten(theme.colors.alpha.black[100], 0.1), 0.5)
              : darken(theme.colors.alpha.black[100], 0.5),
          boxShadow:
            theme.palette.mode === 'dark' ? theme.sidebar.boxShadow : 'none',
        }}
      >
        <Box
          component="nav"
          aria-label="Main navigation"
          id="main-navigation"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            height: '100%',
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Scrollbar regionLabel="Main navigation" regionLabelledBy="main-navigation">
              <Box
                role="button"
                tabIndex={0}
                aria-label="Go to dashboard"
                sx={{
                  width: '100%',
                  px: 4,
                  mt: 3.5,
                  mb: 3,
                  cursor: 'pointer',
                }}
                onClick={handleLogoClick}
                onKeyDown={handleLogoKeyDown}
              >
                <Logo />
              </Box>
              <SidebarMenu />
            </Scrollbar>
          </Box>
          <SidebarFooter />
        </Box>

      </SidebarWrapper>
      <Drawer
        sx={{
          boxShadow: `${theme.sidebar.boxShadow}`,
          '& .MuiDrawer-paper': {
            height: '100%',
          },
        }}
        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
        open={sidebarToggle}
        onClose={closeSidebar}
        variant="temporary"
        elevation={9}
      >
        <SidebarWrapper
          sx={{
            background:
              theme.palette.mode === 'dark'
                ? theme.colors.alpha.white[100]
                : darken(theme.colors.alpha.black[100], 0.5),
          }}
        >
          <Box
            component="nav"
            id="main-navigation-drawer"
            aria-label="Main navigation"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              height: '100%',
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <Scrollbar regionLabel="Main navigation" regionLabelledBy="main-navigation-drawer">
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label="Go to dashboard"
                  sx={{
                    width: '100%',
                    px: 4,
                    my: 3,
                    cursor: 'pointer',
                  }}
                  onClick={handleLogoClick}
                  onKeyDown={handleLogoKeyDown}
                >
                  <Logo />
                </Box>
                <SidebarMenu />
              </Scrollbar>
            </Box>
            <SidebarFooter />
          </Box>
        </SidebarWrapper>
      </Drawer>
    </>
  );
};

export default Sidebar;
